import React, { useState, useEffect, useCallback } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  Link,
  Box,
  Tooltip,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  Collapse
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import FolderIcon from '@mui/icons-material/Folder';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FlutterDashIcon from '@mui/icons-material/FlutterDash';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SportsGolfIcon from '@mui/icons-material/SportsGolf';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import QuizIcon from '@mui/icons-material/Quiz';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MovieIcon from '@mui/icons-material/Movie';
import TrainIcon from '@mui/icons-material/Train';
import InstagramIcon from '@mui/icons-material/Instagram';
import CelebrationIcon from '@mui/icons-material/Celebration';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TerminalIcon from '@mui/icons-material/Terminal';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';

interface Project {
  name: string;
  url: string;
  description: string;
  icon: any;
  backendUrl?: string; // Optional backend API URL to ping
  manualStatus?: 'online' | 'offline'; // If set, skips pinging and uses this status
}

type ProjectStatus = 'checking' | 'online' | 'offline';

interface Props {
  darkMode: boolean;
  toggleDarkMode: () => void;
}

const DirectoryView: React.FC<Props> = ({ darkMode, toggleDarkMode }) => {
  const projects: Project[] = [
    {
      name: "Notes App",
      url: "https://davidgil17.github.io/notes/",
      description: "Take and manage notes directly in your browser",
      icon: NoteAltIcon,
      // Add backend URL if it has one
    },
    {
      name: "Mini Golf Stats",
      url: "https://davidgil17.github.io/mini_golf",
      description: "Mini golf statistics and leaderboard from an epic bachelor party contest",
      icon: SportsGolfIcon,
    },
    {
      name: "Calorie Calculator",
      url: "http://calorie-calculation.info/",
      description: "Calculate your daily caloric needs and track your nutrition goals",
      icon: RestaurantIcon,
    },
    {
      name: "Bird Collector",
      url: "https://davidgil17.github.io/bird-collector",
      description: "A digital collection of bird species with information and tracking features",
      icon: FlutterDashIcon,
    },
    {
      name: "Meeting Scheduler",
      url: "https://meeting-scheduler-frontend-477bc1535ca5.herokuapp.com/",
      description: "Schedule and manage meetings with an easy-to-use calendar interface",
      icon: EventNoteIcon,
      backendUrl: "https://meeting-scheduler-backend-38ca3f0e68df.herokuapp.com/",
      manualStatus: 'offline', // Manually set - change to 'online' when backend is up
    },
    {
      name: "Friendly Bets",
      url: "https://davidgil17.github.io/friendly-bets/",
      description: "Track and manage bets and wagers with friends",
      icon: SportsScoreIcon,
    },
    {
      name: "Guessipe",
      url: "https://davidgil17.github.io/guessipe/",
      description: "Guess recipes based on their ingredients in this fun cooking game",
      icon: QuizIcon,
    },
    {
      name: "Movie Poll",
      url: "https://davidgil17.github.io/movie-poll/",
      description: "Create a movie polling session, invite friends, add movies, and vote on what to watch together",
      icon: MovieIcon,
    },
    {
      name: "Train Time",
      url: "https://davidgil17.github.io/train-time/",
      description: "Track Q train arrival times and schedules",
      icon: TrainIcon,
    },
    {
      name: "Dark Storm TCG",
      url: "https://dark-storm-tcg-game-c25e0035db4e.herokuapp.com/",
      description: "A fantasy trading card game with strategic gameplay and unique card mechanics",
      icon: AutoAwesomeIcon,
      backendUrl: "https://dark-storm-tcg-game-c25e0035db4e.herokuapp.com/",
      manualStatus: 'offline', // Manually set - change to 'online' when backend is up
    },
    {
      name: "Aisle Plan",
      url: "https://davidgil17.github.io/aisle-plan",
      description: "Plan your perfect wedding with collaborative boards and task management",
      icon: CelebrationIcon,
    },
    {
      name: "Text Adventure",
      url: "https://davidgil17.github.io/text-adventure-web/",
      description: "An immersive text adventure RPG with character progression, combat, and exploration",
      icon: TerminalIcon,
    }
  ];

  const [projectStatuses, setProjectStatuses] = useState<Map<string, ProjectStatus>>(new Map());
  const [showOnline, setShowOnline] = useState(true);
  const [showOffline, setShowOffline] = useState(true);

  const pingBackend = async (project: Project): Promise<ProjectStatus> => {
    // If manual status is set, use that instead of pinging
    if (project.manualStatus) {
      return project.manualStatus;
    }

    if (!project.backendUrl) {
      return 'online'; // Projects without backends are considered always online
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

      const response = await fetch(project.backendUrl, {
        method: 'GET',
        mode: 'cors',
        signal: controller.signal,
        cache: 'no-cache',
      });

      clearTimeout(timeoutId);

      // Check if response is OK (status 200-299)
      return response.ok ? 'online' : 'offline';
    } catch (error: any) {
      // Any error (CORS, network, timeout) means we can't reach it properly
      return 'offline';
    }
  };

  const checkAllBackends = useCallback(async () => {
    // Set all to checking first
    const checkingMap = new Map<string, ProjectStatus>();
    projects.forEach(project => {
      checkingMap.set(project.name, 'checking');
    });
    setProjectStatuses(checkingMap);

    // Check all backends in parallel
    const statusPromises = projects.map(async (project) => {
      const status = await pingBackend(project);
      return { name: project.name, status };
    });

    const results = await Promise.all(statusPromises);

    // Update statuses
    const newStatusMap = new Map<string, ProjectStatus>();
    results.forEach(({ name, status }) => {
      newStatusMap.set(name, status);
    });
    setProjectStatuses(newStatusMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkAllBackends();
  }, [checkAllBackends]);

  const onlineProjects = projects.filter(p => projectStatuses.get(p.name) === 'online');
  const offlineProjects = projects.filter(p => projectStatuses.get(p.name) === 'offline');
  const checkingProjects = projects.filter(p => projectStatuses.get(p.name) === 'checking');

  const renderProject = (project: Project, index: number) => {
    const status = projectStatuses.get(project.name);
    
    return (
      <Tooltip 
        key={index}
        title={project.description}
        placement="right"
        arrow
        enterDelay={200}
        leaveDelay={0}
      >
        <ListItem 
          sx={{
            pl: 4,
            '&:hover': {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08)
            }
          }}
          component={Link}
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          color="inherit"
          secondaryAction={
            project.backendUrl && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {status === 'checking' && <CircularProgress size={16} />}
                {status === 'online' && (
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label="Online" 
                    color="success" 
                    size="small"
                    sx={{ 
                      height: 24,
                      '& .MuiChip-label': {
                        display: { xs: 'none', sm: 'block' },
                        px: { sm: 1 }
                      },
                      px: { xs: 0.5, sm: 1 }
                    }}
                  />
                )}
                {status === 'offline' && (
                  <Chip 
                    icon={<CancelIcon />}
                    label="Offline" 
                    color="error" 
                    size="small"
                    sx={{ 
                      height: 24,
                      '& .MuiChip-label': {
                        display: { xs: 'none', sm: 'block' },
                        px: { sm: 1 }
                      },
                      px: { xs: 0.5, sm: 1 }
                    }}
                  />
                )}
              </Box>
            )
          }
        >
          <ListItemIcon>
            <project.icon color="secondary" />
          </ListItemIcon>
          <ListItemText 
            primary={project.name}
            primaryTypographyProps={{
              sx: {
                fontFamily: 'monospace',
                '&:hover': {
                  color: 'primary.main'
                }
              }
            }}
          />
        </ListItem>
      </Tooltip>
    );
  };

  return (
    <>
    <Tooltip title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'} arrow placement="left">
      <IconButton
        onClick={toggleDarkMode}
        sx={{
          position: 'fixed',
          top: 16,
          right: 16,
          zIndex: 1200,
          bgcolor: 'background.paper',
          boxShadow: 2,
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            transform: 'scale(1.1)',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
      </IconButton>
    </Tooltip>
    <Box sx={{
      maxWidth: 600,
      margin: 'auto',
      mt: 4,
      p: 3
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 2
      }}>
        <Typography variant="h4" sx={{
          fontFamily: 'monospace',
          color: 'primary.main'
        }}>
          /projects
        </Typography>
        <Tooltip title="Follow @davidbirds on Instagram" arrow>
          <IconButton
            component={Link}
            href="https://www.instagram.com/davidbirds/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#E4405F',
              '&:hover': {
                bgcolor: 'rgba(228, 64, 95, 0.1)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease-in-out'
            }}
          >
            <InstagramIcon fontSize="large" />
          </IconButton>
        </Tooltip>
      </Box>
      
      <Paper elevation={3} sx={{
        borderRadius: 2
      }}>
        <List>
          <ListItem>
            <ListItemIcon>
              <FolderIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="projects" />
          </ListItem>

          {/* Checking Projects */}
          {checkingProjects.length > 0 && (
            <>
              <ListItem 
                onClick={() => {}}
                sx={{
                  pl: 4,
                  cursor: 'default',
                  bgcolor: 'action.hover'
                }}
              >
                <ListItemText 
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                        Checking... ({checkingProjects.length})
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
              {checkingProjects.map((project, index) => renderProject(project, index))}
            </>
          )}

          {/* Online Projects */}
          {onlineProjects.length > 0 && checkingProjects.length === 0 && (
            <>
              <ListItem 
                button
                onClick={() => setShowOnline(!showOnline)}
                sx={{ 
                  pl: 4,
                  bgcolor: 'rgba(76, 175, 80, 0.08)'
                }}
              >
                <ListItemIcon>
                  {showOnline ? <ExpandLessIcon color="success" /> : <ExpandMoreIcon color="success" />}
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#4caf50' }}>
                      Online ({onlineProjects.length})
                    </Typography>
                  }
                />
              </ListItem>
              <Collapse in={showOnline}>
                {onlineProjects.map((project, index) => renderProject(project, index))}
              </Collapse>
            </>
          )}

          {/* Offline Projects */}
          {offlineProjects.length > 0 && checkingProjects.length === 0 && (
            <>
              <Divider />
              <ListItem 
                button
                onClick={() => setShowOffline(!showOffline)}
                sx={{ 
                  pl: 4,
                  bgcolor: 'rgba(244, 67, 54, 0.08)'
                }}
              >
                <ListItemIcon>
                  {showOffline ? <ExpandLessIcon color="error" /> : <ExpandMoreIcon color="error" />}
                </ListItemIcon>
                <ListItemText 
                  primary={
                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#f44336' }}>
                      Offline ({offlineProjects.length})
                    </Typography>
                  }
                />
              </ListItem>
              <Collapse in={showOffline}>
                {offlineProjects.map((project, index) => renderProject(project, index))}
              </Collapse>
            </>
          )}
        </List>
      </Paper>
    </Box>
    </>
  );
};

export default DirectoryView; 